import axios from "axios";
import { BASE_API_URL } from "../consts";
import type { Reservation } from "./reservationsAPI";

export type VenueEventType = "concert" | "standup" | "band" | "private" | "sports" | "other";
export type VenueEventStatus = "draft" | "published" | "cancelled" | "completed" | "archived";

export interface VenueEvent {
  _id: string;
  clientId: string;
  title: string;
  description?: string | null;
  type: VenueEventType;
  date: string;
  time: string;
  endTime?: string | null;
  totalCapacity: number;
  totalTables?: number | null;
  minAdvanceBookingDays?: number | null;
  tableLayout?: { number: number; x: number; y: number }[] | null;
  floorPlanBg?: "grid" | "image";
  ticketPrice: number;
  currency: string;
  status: VenueEventStatus;
  isLocked?: boolean;
  lockedGuestCount?: number | null;
  lockedPrice?: number | null;
  lockedPriceType?: "per_person" | "flat" | null;
  reservedCount: number;
  reservationCount: number;
  reservedTables?: number;
  freeTables?: number | null;
  responsibleUserId?: { _id: string; firstName: string; lastName: string } | string | null;
  kitchenUserId?: { _id: string; firstName: string; lastName: string } | string | null;
  createdBy?: { _id: string; firstName: string; lastName: string } | null;
  createdAt: string;
  updatedAt: string;
}

export const getVenueEvents = (clientId: string, params?: { status?: string; from?: string; to?: string }) =>
  axios.get<VenueEvent[]>(`${BASE_API_URL}/${clientId}/venue-events`, { params });

export const getVenueEventById = (id: string) =>
  axios.get<VenueEvent>(`${BASE_API_URL}/venue-events/${id}`);

export const createVenueEvent = (clientId: string, data: Partial<VenueEvent>) =>
  axios.post<VenueEvent>(`${BASE_API_URL}/${clientId}/venue-events`, data);

export const updateVenueEvent = (id: string, data: Partial<VenueEvent>) =>
  axios.put<VenueEvent>(`${BASE_API_URL}/venue-events/${id}`, data);

export const deleteVenueEvent = (id: string) =>
  axios.delete(`${BASE_API_URL}/venue-events/${id}`);

export const archiveVenueEvent = (id: string) =>
  axios.patch<VenueEvent>(`${BASE_API_URL}/venue-events/${id}/archive`);

export const duplicateVenueEvent = (id: string) =>
  axios.post<VenueEvent>(`${BASE_API_URL}/venue-events/${id}/duplicate`);

export const getEventReservations = (eventId: string) =>
  axios.get<Reservation[]>(`${BASE_API_URL}/venue-events/${eventId}/reservations`);

// ─── Public ───────────────────────────────────────────────────────────────────

export interface PublicVenueEvent extends VenueEvent {
  available: number;
  reservedTableNumbers: number[];
}

export const getPublicVenueEvents = (clientId: string) =>
  axios.get<PublicVenueEvent[]>(`${BASE_API_URL}/public/venue-events`, { params: { clientId } });

export const getPublicVenueEventById = (eventId: string) =>
  axios.get<PublicVenueEvent>(`${BASE_API_URL}/public/venue-events/${eventId}`);

export const bookPublicEvent = (
  eventId: string,
  data: { guestName: string; guestPhone: string; guestEmail?: string; ticketsCount: number; note?: string; eventTableNumber?: number }
) => axios.post(`${BASE_API_URL}/public/venue-events/${eventId}/book`, data);
